'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface HeroSectionProps {
  className?: string;
}

interface HeroContent {
  badge?: { icon: string; text: string };
  headline: string;
  subheadline: string;
  tagline: string;
  primaryCTA: { text: string; link: string };
  secondaryCTA: { text: string; link: string };
  features: Array<{ icon: string; text: string }>;
}

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  const [content, setContent] = useState<HeroContent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const supabase = createClient();
      
      // Wait for session to be ready before making queries
      await supabase.auth.getSession();
      
      const { data, error } = await supabase
        .from('content_pages')
        .select('content')
        .eq('slug', 'homepage_hero')
        .eq('status', 'published')
        .maybeSingle();

      if (error) throw error;
      
      if (data?.content) {
        const parsedContent = typeof data.content === 'string' 
          ? JSON.parse(data.content) 
          : data.content;
        setContent(parsedContent);
      }
    } catch (error) {
      console.error('Error loading hero content:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !content) {
    return (
      <section className={`relative bg-gradient-to-br from-surface via-white to-muted overflow-hidden ${className}`}>
        <div className="relative mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-3/4 mx-auto"></div>
              <div className="h-12 bg-muted rounded w-full"></div>
              <div className="h-6 bg-muted rounded w-2/3 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`relative bg-gradient-to-br from-surface via-white to-muted overflow-hidden ${className}`}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
      </div>

      <div className="relative mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="max-w-4xl mx-auto text-center">
          {content.badge && (
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-border rounded-full mb-8 shadow-sm">
              <Icon name={content.badge.icon as any} size={20} variant="solid" className="text-success" />
              <span className="font-accent text-xs text-text-secondary">
                {content.badge.text}
              </span>
            </div>
          )}

          <h1 className="font-headline font-bold text-4xl lg:text-6xl text-primary mb-6 leading-tight">
            {content.headline}
          </h1>

          <p className="font-body text-xl lg:text-2xl text-text-secondary mb-4 leading-relaxed">
            {content.subheadline}
          </p>

          <p className="font-headline font-semibold text-2xl lg:text-3xl text-accent mb-12">
            {content.tagline}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-16">
            <Link
              href={content.primaryCTA.link}
              className="w-full sm:w-auto px-8 py-4 bg-cta text-cta-foreground font-headline font-semibold text-base rounded-sm hover:bg-accent transition-colors duration-smooth shadow-institutional"
            >
              {content.primaryCTA.text}
            </Link>
            <Link
              href={content.secondaryCTA.link}
              className="w-full sm:w-auto px-8 py-4 bg-white text-primary border-2 border-primary font-headline font-semibold text-base rounded-sm hover:bg-surface transition-colors duration-smooth"
            >
              {content.secondaryCTA.text}
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
            {content.features.map((feature, index) => (
              <div key={index} className="flex flex-col items-center">
                <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mb-3">
                  <Icon name={feature.icon as any} size={24} variant="solid" className="text-success" />
                </div>
                <p className="font-body text-sm text-text-primary font-semibold">
                  {feature.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;